import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Globe, CheckCircle2, Loader2, AlertCircle, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";

export default function CustomDomainManager() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newDomain, setNewDomain] = useState("");

  const fetchStatus = useCallback(async () => {
    try {
      const r = await api.get("/tenants/mine/custom-domain");
      setData(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 20000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const handleAdd = async () => {
    if (!newDomain.trim()) return;
    setSubmitting(true);
    try {
      await api.put("/tenants/mine/custom-domain", { domain: newDomain });
      toast.success("Dominio registrato. Configura il CNAME come indicato.");
      setNewDomain("");
      fetchStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Confermi la rimozione del dominio personalizzato?")) return;
    try {
      await api.delete("/tenants/mine/custom-domain");
      toast.success("Dominio rimosso");
      fetchStatus();
    } catch (e) { toast.error("Errore rimozione"); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copiato"); };

  if (loading) return <div className="p-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>;

  if (!data?.configured) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-5">
        <div className="flex items-center gap-2 text-amber-900 font-semibold mb-1"><AlertCircle className="w-4 h-4" /> Cloudflare for SaaS non configurato</div>
        <p className="text-sm text-amber-800">Il SuperAdmin deve impostare in <code className="px-1 bg-white rounded">backend/.env</code>: <code className="px-1 bg-white rounded">CLOUDFLARE_API_TOKEN</code>, <code className="px-1 bg-white rounded">CLOUDFLARE_ZONE_ID</code>, <code className="px-1 bg-white rounded">CLOUDFLARE_FALLBACK_ORIGIN</code>.</p>
      </div>
    );
  }

  if (!data?.current_domain) {
    return (
      <div className="bg-white border border-border rounded-sm p-6" data-testid="custom-domain-empty">
        <div className="flex items-center gap-3 mb-3"><Globe className="w-5 h-5 text-brand" /><h3 className="font-heading font-bold text-lg">Dominio personalizzato</h3></div>
        <p className="text-sm text-muted-foreground mb-4">Collega il tuo dominio (es. <code>mio-negozio.it</code>) alla tua vetrina. SSL automatico incluso via Cloudflare.</p>
        <div className="flex gap-2">
          <input value={newDomain} onChange={(e) => setNewDomain(e.target.value)}
            placeholder="mio-negozio.it" data-testid="custom-domain-input"
            className="flex-1 px-4 h-11 border border-border rounded-sm text-sm" />
          <button onClick={handleAdd} disabled={submitting || !newDomain.trim()} data-testid="custom-domain-add"
            className="h-11 px-5 bg-foreground text-white text-sm rounded-sm font-medium hover:bg-brand disabled:opacity-50">
            {submitting ? "..." : "Collega"}
          </button>
        </div>
      </div>
    );
  }

  if (data.status === "active") {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-5" data-testid="custom-domain-active">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            <div>
              <h3 className="font-heading font-bold text-emerald-900 text-lg">{data.current_domain}</h3>
              <p className="text-sm text-emerald-700">Attivo · SSL valido</p>
            </div>
          </div>
          <button onClick={handleDelete} data-testid="custom-domain-delete" className="p-2 text-red-600 hover:bg-red-50 rounded-sm"><Trash2 className="w-5 h-5" /></button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-sm p-5" data-testid="custom-domain-pending">
      <div className="flex items-center gap-2 mb-4 text-amber-900">
        <Loader2 className="w-5 h-5 animate-spin" />
        <h3 className="font-heading font-bold">In attesa di configurazione DNS — {data.current_domain}</h3>
      </div>
      <div className="bg-white border border-border rounded-sm p-4 mb-3">
        <p className="text-sm mb-3 font-medium">Vai sul DNS del tuo registrar e aggiungi questo record CNAME:</p>
        <div className="space-y-1.5 text-sm font-mono">
          <Row k="Tipo" v="CNAME" />
          <Row k="Nome" v={data.current_domain} />
          <Row k="Valore" v={data.cname_target} onCopy={copy} />
        </div>
      </div>
      <div className="text-xs text-amber-800 space-y-0.5">
        <div>Stato DNS: <strong>{data.cloudflare_status}</strong> · SSL: <strong>{data.ssl_status}</strong></div>
        {data.verification_errors?.length > 0 && (
          <div className="bg-red-100 border border-red-200 rounded-sm p-2 mt-2 text-red-800 text-xs"><AlertCircle className="w-3 h-3 inline mr-1" />{data.verification_errors.join("; ")}</div>
        )}
        <p className="mt-2">Pagina aggiornata ogni 20s. Tempo medio: 5-30 minuti.</p>
      </div>
      <button onClick={handleDelete} className="mt-3 text-sm text-red-600 hover:underline inline-flex items-center gap-1"><Trash2 className="w-4 h-4" /> Rimuovi</button>
    </div>
  );
}

const Row = ({ k, v, onCopy }) => (
  <div className="flex justify-between items-center bg-neutral-50 px-3 py-2 rounded">
    <span className="text-muted-foreground">{k}:</span>
    {onCopy ? (
      <button onClick={() => onCopy(v)} className="font-semibold inline-flex items-center gap-1.5 hover:text-brand">{v}<Copy className="w-3.5 h-3.5" /></button>
    ) : <span className="font-semibold">{v}</span>}
  </div>
);
